import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Plus } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { SelectField } from "@/lib/crm";
import { PageHeader, Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const TONE = { attiva: "green", sospesa: "amber", disattivata: "neutral" };
const TRIGGER = { evento: "Evento", stato: "Stato", scadenza: "Data/scadenza", condizione: "Condizione", ricorrenza: "Ricorrenza" };

const Runs = ({ a, onClose }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { if (a) api.get(`/automations/${a.id}/runs`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [a]);
  return (
    <Sheet open={!!a} onOpenChange={(o) => !o && onClose()}><SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="automation-runs">
      <SheetHeader><SheetTitle>Storico · {a?.name}</SheetTitle></SheetHeader>
      <ul className="mt-4 divide-y divide-line">{items.map((r) => <li key={r.id} className="py-2.5 text-sm" data-testid={`automation-run-${r.id}`}>
        <StatusBadge tone={r.status === "ok" ? "green" : "red"}>{r.status}</StatusBadge>
        <span className="ml-2 text-slate-600">{fmtDateTime(r.started_at)} · {r.trigger} · {r.matched ?? 0} record, {r.notified ?? 0} notifiche</span>
        {r.error && <p className="mt-1 text-xs text-red-600">{r.error}</p>}</li>)}
        {!items.length && <li className="py-2 text-sm text-slate-500">Nessuna esecuzione.</li>}</ul>
    </SheetContent></Sheet>
  );
};

export default function AutomationsPage() {
  const [items, setItems] = useState(null);
  const [tpl, setTpl] = useState([]);
  const [pick, setPick] = useState("");
  const [runs, setRuns] = useState(null);
  const load = () => api.get("/automations").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/automations/templates").then((r) => setTpl(r.data.items)).catch(() => {}); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errorMessage(e)); } };
  return (
    <div data-testid="automations-page">
      <PageHeader overline="Amministrazione" title="Automazioni" testId="automations-header"
        description="Workflow controllati sulle logiche esistenti: generano solo notifiche in-app, senza duplicati." />
      <Panel className="mb-6 flex flex-wrap items-end gap-3 p-5">
        <SelectField className="min-w-[260px] flex-1" label="Nuova automazione da modello" value={pick} onChange={setPick} options={tpl.map((t) => ({ key: t.key, label: t.name }))} testId="automation-template" />
        <Button disabled={!pick} className="gap-1" onClick={() => act(() => api.post("/automations", { template: pick }), "Automazione creata")} data-testid="automation-create"><Plus className="h-4 w-4" />Crea</Button>
      </Panel>
      <div className="space-y-3">{(items || []).map((a) => (
        <Panel key={a.id} className="p-5" data-testid={`automation-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0"><p className="font-semibold text-ink">{a.name}</p><p className="text-sm text-slate-500">{a.description}</p>
              <p className="mt-1 text-xs text-slate-500">Trigger {TRIGGER[a.trigger]} · {a.frequency} · destinatari {a.recipient === "me" ? "solo io" : "responsabili dei record"}</p></div>
            <StatusBadge tone={TONE[a.status]} data-testid={`automation-status-${a.id}`}>{a.status}</StatusBadge>
          </div>
          <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-3">
            <span>Ultima: {a.last_run ? fmtDateTime(a.last_run) : "—"}</span><span>Prossima: {a.next_run ? fmtDateTime(a.next_run) : "—"}</span>
            <span className={a.last_error ? "text-red-600" : ""}>Esito: {a.last_error || a.last_result || "—"}</span></div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["attiva", "sospesa", "disattivata"].filter((s) => s !== a.status).map((s) => <Button key={s} size="sm" variant="outline" className="rounded-full"
              onClick={() => act(() => api.post(`/automations/${a.id}/status`, { status: s }), `Automazione ${s}`)} data-testid={`automation-set-${s}-${a.id}`}>{s === "attiva" ? "Attiva" : s === "sospesa" ? "Sospendi" : "Disattiva"}</Button>)}
            <Button size="sm" variant="outline" className="gap-1 rounded-full" onClick={() => act(() => api.post(`/automations/${a.id}/run`), "Esecuzione completata")} data-testid={`automation-run-${a.id}`}><Play className="h-3.5 w-3.5" />Esegui ora</Button>
            <Button size="sm" variant="ghost" onClick={() => setRuns(a)} data-testid={`automation-history-${a.id}`}>Storico</Button>
          </div>
        </Panel>))}
        {items && !items.length && <Panel className="p-5"><SectionLabel>Nessuna automazione</SectionLabel></Panel>}</div>
      <Runs a={runs} onClose={() => setRuns(null)} />
    </div>
  );
}
